import React, { useState, useEffect, FC } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Row, Col, Button, Alert } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import { Icon } from "../../../components/Component";
import toolMasterApis from "../../../api/master/toolMaster";
import useSequence from "../../../hooks/useSequence";

interface SelectOptions {
  label: string;
  value: string | number;
}

const ToolMasterForm: FC<{
  id?: string | null;
  formAction?: string | null;
  toggleModelPopUp: () => void;
  refreshData?: () => void;
}> = ({ id, formAction, toggleModelPopUp, refreshData }) => {
  const [action, setAction] = useState(formAction);
  const [toolDetail, setToolDetail] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [toolTypes, setToolTypes] = useState<SelectOptions[]>([]);
  const [alertInfo, setAlertInfo] = useState<{
    type: string;
    message: string;
    show: boolean;
  }>({
    type: "",
    message: "",
    show: false,
  });

  const { hasSequence } = useSequence(172);
  const methods = useForm({ mode: "onChange" });
  const { handleSubmit, setValue, getValues, watch } = methods;

  const selectedToolType = watch("toolTypeId");

  const fetchToolTypes = async () => {
    try {
      const response = await toolMasterApis.toolTypesList();
      if (response?.status) {
        const options = response.data.map((t: any) => ({
          label: t.typeName,
          value: t.toolTypeId,
        }));
        setToolTypes(options);
      }
    } catch (error) {
      console.error("Error fetching tool types:", error);
    }
  };

  const fetchToolDetails = async (toolId: any) => {
    try {
      setLoading(true);
      const response = await toolMasterApis.fetchToolById(toolId);
      if (response?.status) {
        const data = response.data;
        setToolDetail(data);
        setValue("sequenceId", data.sequenceId);
        setValue("toolName", data.toolName);
        setValue("description", data.description || "");
        setValue("toolTypeId", data.toolTypeId);
        if (data.typeName === "Others" && data.customType) {
          setValue("customType", data.customType);
        }
      } else {
        showAlertMessage("error", "Failed to load tool details.");
      }
    } catch (error) {
      showAlertMessage("error", "Error fetching tool details.");
    } finally {
      setLoading(false);
    }
  };

  const showAlertMessage = (type: string, message: string) => {
    setAlertInfo({ type, message, show: true });
  };

  const hideAlert = () => {
    setAlertInfo((prev) => ({ ...prev, show: false }));
  };

  const hasFormChanges = () => {
    if (action !== "edit" || !id) return true;
    if (!toolDetail || Object.keys(toolDetail).length === 0) return true;

    const current = getValues();
    const currentValues = {
      toolName: current.toolName || "",
      description: current.description || "",
      toolTypeId: Number(current.toolTypeId || "") || "",
      customType: current.customType || "",
    };

    const originalValues = {
      toolName: toolDetail.toolName || "",
      description: toolDetail.description || "",
      toolTypeId: Number(toolDetail.toolTypeId || "") || "",
      customType: toolDetail.customType || "",
    };

    return (
      currentValues.toolName !== originalValues.toolName ||
      currentValues.description !== originalValues.description ||
      currentValues.toolTypeId !== originalValues.toolTypeId ||
      currentValues.customType !== originalValues.customType
    );
  };

  const onSubmit = async (_data: any) => {
    try {
      setLoading(true);
      hideAlert();

      if (!hasSequence && (!_data.sequenceId || _data.sequenceId.trim() === "")) {
        methods.setError("sequenceId", {
          type: "required",
          message: "Tool ID is required",
        });
        setLoading(false);
        return;
      }

      const current = getValues();
      const payload: any = {
        toolName: current.toolName,
        description: current.description || "",
        toolTypeId: current.toolTypeId,
      };

      const selectedType = toolTypes.find(
        (t) => t.value === current.toolTypeId
      );
      if (selectedType?.label === "Others" && current.customType) {
        payload.customType = current.customType;
      }

      if (!hasSequence && action !== "edit") {
        payload.sequenceId = _data.sequenceId;
      }
      if (action === "edit" && id) {
        if (
          toolDetail &&
          Object.keys(toolDetail).length > 0 &&
          !hasFormChanges()
        ) {
          showAlertMessage("error", "No Changes. No fields were modified.");
          setTimeout(() => hideAlert(), 1500);
          setLoading(false);
          return;
        }
      }

      let response;
      if (action === "edit" && id) {
        response = await toolMasterApis.updateToolById(Number(id), payload);
      } else {
        response = await toolMasterApis.createTool(payload);
      }

      if (response?.status) {
        showAlertMessage(
          "success",
          action === "edit"
            ? "Tools & Machinery has been updated"
            : "Tools & Machinery has been created"
        );

        setTimeout(() => {
          toggleModelPopUp();
          refreshData?.();
        }, 1500);
      } else {
        const msg = response?.message || "Something went wrong!";
        showAlertMessage("error", msg);
        setTimeout(() => hideAlert(), 1500);
      }
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || error?.message;
      showAlertMessage("error", errMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchToolTypes();
    if (id && action === "edit") {
      fetchToolDetails(id);
    }
  }, [id, action]);

  useEffect(() => {
    return () => {
      hideAlert();
    };
  }, []);

  const selectedTypeObj = toolTypes.find((t) => t.value === selectedToolType);
  const showCustomType = selectedTypeObj?.label === "Others";

  if (
    loading &&
    action === "edit" &&
    id &&
    (!toolDetail || Object.keys(toolDetail).length === 0)
  ) {
    return (
      <div className="text-center p-4">
        <Icon name="spinner" className="spin" /> Loading...
      </div>
    );
  }

  return (
    <>
      <h5 className="title">
        {action === "edit"
          ? `Edit ${toolDetail.toolName || "Tool Category"}`
          : "Create New Tool Category"}
      </h5>

      <Alert
        color={alertInfo.type === "error" ? "danger" : "success"}
        className="alert-icon"
        isOpen={alertInfo.show}
        style={{
          textAlign: "left",
          fontSize: "14px",
          cursor: "default",
          marginBottom: "1rem",
          marginTop: "0.5rem",
        }}
      >
        <Icon
          name={alertInfo.type === "error" ? "alert-circle" : "check-circle"}
        />
        <span>{alertInfo.message}</span>
      </Alert>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            {!hasSequence && (
              <Col md={6}>
                <FormInput
                  name="sequenceId"
                  label="Tool ID"
                  required={true}
                  type="text"
                  pattern={/^[a-zA-Z0-9./_-]+$/}
                  maxLength={30}
                  disabled={action === "edit"}
                  placeholder="Enter Tool ID"
                />
              </Col>
            )}

            <Col md={6}>
              <FormSelect
                name="toolTypeId"
                label="Type"
                options={toolTypes}
                required={true}
                placeholder="Select Type"
                disabled={action === "edit"}
              />
            </Col>

            {showCustomType && (
              <Col md={6}>
                <FormInput
                  name="customType"
                  label="Custom Type"
                  type="text"
                  placeholder="Enter custom type"
                  required={true}
                  maxLength={100}
                  disabled={action === "edit"}
                />
              </Col>
            )}

            <Col md={6}>
              <FormInput
                name="toolName"
                label="Name"
                type="text"
                placeholder="Enter Name"
                required={true}
                maxLength={100}
              />
            </Col>

            <Col md={12} className="mt-2">
              <FormInput
                name="description"
                label="Description"
                type="textarea"
                placeholder="Write your description"
                required={false}
              />
            </Col>
          </Row>

          <Row className="mt-4">
            <Col lg="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                <li>
                  <Button
                    className="btn btn-primary btn-md"
                    type="submit"
                    color="primary"
                    disabled={loading}
                  >
                    <span>{action === "edit" ? "Update" : "Submit"}</span>
                  </Button>
                </li>
                <li>
                  <Button
                    color="light"
                    outline
                    onClick={(ev) => {
                      ev.preventDefault();
                      toggleModelPopUp();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </li>
              </ul>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </>
  );
};

export default ToolMasterForm;
